import {
  get,
  first,
  filter,
  isEmpty,
  reduce,
  includes,
  size,
  has,
  find,
  map,
  max
} from 'lodash-es';
import moment from 'moment';
import {createSelector} from 'reselect';

import {
  getAllLicenses,
  SUBJECT_PRODUCTS,
} from 'modulesV2/utils/licenseUtils';
import {applyTo, applier} from 'utils/lodashHelpers';

// Helper functions for the getUserProperties selector.
// The getUser helper function gets the user off the state.
const getUser = (state) => get(state, 'user', null);
const getUserId = (state) => get(getUser(state), 'id');
const getIsUserLoading = (state) => get(state, 'user.is_loading', null);

// Memoized user properties. These properties relate specifically to the "sales"
// side of the user - everything to do with their licensing information.
const getUserProperties = createSelector(
  [getUser],
  (user) => {
    // The following are helper functions that keep specific pieces of logic
    // together with a descriptive name for each.
    const getDaysSinceJoining = () => {
      const now = moment();
      const dateJoined = moment(get(user, 'date_joined'));
      return Math.trunc(moment.duration(now.diff(dateJoined)).asDays());
    };
    const getExpiredLicense = (product) => {
      if (!user) return;
      const mostRecentLicense = first(getAllLicenses(user, {product, onlyActive: false}));
      const isExpired = get(mostRecentLicense, 'is_expired');
      const isSubscription = get(mostRecentLicense, 'is_subscription');
      if (isSubscription && isExpired) {
        // return boolean used for masthead settings cta
        return isExpired;
      }
    };
    const getActiveLicense = (product) => {
      if (!user) return;
      return first(getAllLicenses(user, {product, onlyActive: true}));
    };
    const getCurrentAndFutureLicenses = (product) => {
      if (!user) return;
      return filter(getAllLicenses(user, {product: product, onlyActive: false}),
        (license) => {
          return moment(license.date_ends).diff(moment(), 'days') > 0;
        });
    };
    const getDaysLeftOnLongestLicense = (product) => {
      const currentAndFutureLicenses = getCurrentAndFutureLicenses(product);
      return applyTo(currentAndFutureLicenses)(
        applier(map)((license) => {
          return moment(get(license, 'date_ends')).diff(moment(), 'days');
        }),
        applier(max)()
      );
    };

    const canViewCollection = (slug) => {
      return !isEmpty(user.licensed_collections) && user.licensed_collections.some(
        (collection) => collection.slug === slug);
    };

    const getDaysLeftInTrial = (license) => {
      const now = moment();
      if (license && license.is_trial) {
        const trialEndDate = moment(license.date_ends);
        const daysLeft = moment.duration(trialEndDate.diff(now)).asDays();
        return Math.trunc(daysLeft);
      }
    };

    const getTourEvent = (name) => {
      if (isEmpty(user)) return null;
      return get(user.tour_events, name, {
        is_skipped: false,
        step: 1
      });
    };

    const getPreferredMaturityBands = (teacher = null) => {
      if (isEmpty(user) || !user.teacher) return null;
      teacher = teacher || user.teacher;
      const MATURITY_BANDS = window.CONTENT_MATURITY_GRADE_LEVELS;
      const lower_grade_bound = teacher.lowest_preferred_grade_level;
      const upper_grade_bound = teacher.highest_preferred_grade_level;
      if (!lower_grade_bound && !upper_grade_bound) {
        return Object.keys(MATURITY_BANDS);
      } else {
        const preferredBands = [];
        for (const band in MATURITY_BANDS) {
          const preferredGradeRange = [];
          for (let i = lower_grade_bound; i <= upper_grade_bound; i++) {
            preferredGradeRange.push(i);
          }
          if (preferredGradeRange.includes(MATURITY_BANDS[band][0]) && preferredGradeRange.includes(MATURITY_BANDS[band][1])) {
            preferredBands.push(band);
          }
        }
        return preferredBands;
      }
    };

    const getFeatureState = (name) => get(user, ['features', name]) || 'off';
    const hasFeature = (name) => getFeatureState(name) == 'on';
    const userProductsDict = reduce(user.licenses, (acc, license) => {
      if (includes(SUBJECT_PRODUCTS, license.product) && license.is_active) {
        return {
          ...acc,
          [license.product]: true,
        };
      }
      return acc;
    }, {});
    const licensedToOneProductOnly = (product) => product in userProductsDict && size(userProductsDict) === 1;
    const isEssentialsOnlyUser = licensedToOneProductOnly('essentials');
    const canViewOneOrMoreSubjectProducts = (
      canViewCollection('ela') ||
      canViewCollection('ss') ||
      canViewCollection('sci')
    );
    // These are the actual properties that the selector returns.
    const isUserLoading = get(user, 'is_loading');
    const isAuthenticated = get(user, 'is_authenticated', false);
    // User roles
    const isTeacher = get(user, 'teacher.role') == 'teacher';
    const isParent = get(user, 'is_authenticated', false) && get(user, 'teacher.role') == 'parent';
    const isStudent = has(user, 'student.id');
    const isPrincipal = get(user, 'principal', false);
    const isDistrictLeader = get(user, 'district_leader', false);
    const isEducator = (
      get(user, 'is_authenticated', false) &&
      has(user, 'teacher') &&
      get(user, 'teacher.role') != 'parent'
    );

    const getUserRole = () => {
      if (isDistrictLeader) {
        return 'district_leader';
      } else if (isPrincipal) {
        return 'principal';
      } else if (isTeacher) {
        return 'teacher';
      } else if (isParent) {
        return 'caregiver';
      } else if (isStudent) {
        return 'student';
      } else {
        return null;
      }
    };

    const schoolId = get(user, 'teacher.school.id');
    // License states
    const activeProLicense = getActiveLicense('pro');
    const activeEssentialsLicense = !!getActiveLicense('essentials');
    const activeELALicense = !!getActiveLicense('ela');
    const activeScienceLicense = !!getActiveLicense('science');
    const activeSSLicense = !!getActiveLicense('social_studies');
    const isTrialUser = get(activeProLicense, 'is_trial', false);
    const isFreeUser = isEmpty(getAllLicenses(user, {}));
    const hasExtensionTrial = !!find(user.licenses, (license) => license.is_trial_extension);
    const isStaff = !!get(user, 'is_staff');
    const daysSinceJoining = getDaysSinceJoining();
    const canViewPowerWords = hasFeature('view_power_words');
    const daysLeftInTrial = getDaysLeftInTrial(activeProLicense);
    const isUserStateValidated = (
      get(user, 'teacher.validation_state') == 'valid' ||
      get(user, 'principal.validation_state', 'invalid') == 'valid'
    );
    const isAuthorizedPrincipal = isPrincipal && isUserStateValidated;
    const isAuthorizedDistrictLeader = get(user, 'district_leader.date_accepted_terms_of_use', false);
    const headerCount = get(user, 'header_count', null);
    const getLicenseStatus = () => {
      let license_status = null;
      if (isTrialUser) {
        license_status = 'trial';
      } else if (isFreeUser) {
        license_status = 'free';
      } else if (activeELALicense && activeScienceLicense && activeSSLicense) {
        license_status = 'all subject products';
      } else if (activeELALicense) {
        license_status = 'ela';
      } else if (activeScienceLicense) {
        license_status = 'science';
      } else if (activeSSLicense) {
        license_status = 'social studies';
      } else if (isEssentialsOnlyUser) {
        license_status = 'essentials';
      }
      return license_status;
    };
    const licenseStatus = getLicenseStatus();
    const hasActiveProductLicense = [activeEssentialsLicense, activeELALicense, activeScienceLicense, activeSSLicense]
      .some((isActive) => isActive == true);

    const isTxTeacher = (get(user, 'teacher.school.address.state', '') === 'TX');

    // teacher binder definition for a teacher who can view TEKS reading skills
    const isTeksTeacher = isTxTeacher && canViewCollection('ela');

    return {
      canViewCollection,
      canViewOneOrMoreSubjectProducts,
      canViewPowerWords,
      daysLeftInTrial,
      daysSinceJoining,
      getActiveLicense,
      getCurrentAndFutureLicenses,
      getDaysLeftOnLongestLicense,
      getDaysLeftInTrial,
      getExpiredLicense,
      getPreferredMaturityBands,
      getTourEvent,
      getFeatureState,
      hasFeature,
      licensedToOneProductOnly,
      licenseStatus,
      headerCount,
      isAuthenticated,
      isTrialUser,
      isFreeUser,
      isEducator,
      isStudent,
      isParent,
      isTeacher,
      isPrincipal,
      getUserRole,
      isUserStateValidated,
      isAuthorizedPrincipal,
      isAuthorizedDistrictLeader,
      hasExtensionTrial,
      isUserLoading,
      isDistrictLeader,
      schoolId,
      isStaff,
      isEssentialsOnlyUser,
      isTxTeacher,
      isTeksTeacher,
      activeEssentialsLicense,
      activeELALicense,
      activeScienceLicense,
      activeSSLicense,
      hasActiveProductLicense,
    };
  },
);

export {getUser, getUserProperties, getUserId, getIsUserLoading};
